import { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, Minus, Plus, Phone, MessageSquare, Check } from 'lucide-react';
import { shopApi, cartApi, cartStorage, tokenStorage } from '@/user/api';
import type { ProductVO } from '@/types/api-entity';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import ProductImageGallery from '@/components/ProductImageGallery';

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [product, setProduct] = useState<ProductVO | null>(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [activeTab, setActiveTab] = useState<'specs' | 'features'>('specs');
  const [cartCount, setCartCount] = useState(cartStorage.getCartCount());
  const [addedTip, setAddedTip] = useState(false);

  useEffect(() => {
    if (!id) return;
    setLoading(true);
    shopApi
      .getProductDetail(parseInt(id))
      .then((res) => {
        if (res.code === 200 && res.data) {
          setProduct(res.data);
        }
      })
      .finally(() => setLoading(false));
  }, [id]);

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <div className="w-8 h-8 border-2 border-[#1c5bff] border-t-transparent rounded-full animate-spin mb-4" />
        <p className="text-gray-500">加载中...</p>
      </div>
    );
  }

  if (!product) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-gray-50">
        <p className="text-gray-500 mb-4">商品不存在</p>
        <Link to="/shop" className="text-[#1c5bff] hover:underline">
          返回商城
        </Link>
      </div>
    );
  }

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('zh-CN', {
      style: 'currency',
      currency: 'CNY',
      minimumFractionDigits: 0,
    }).format(price);
  };

  const specs = product.specifications
    ? Object.entries(product.specifications).map(([label, value]) => ({ label, value }))
    : [];

  const handleAddToCart = async () => {
    if (tokenStorage.isLoggedIn()) {
      try {
        await cartApi.addToCart(product.id, quantity);
        setCartCount((prev) => prev + quantity);
        setAddedTip(true);
        setTimeout(() => setAddedTip(false), 2000);
      } catch {
        cartStorage.addToCart(product, quantity);
        setCartCount(cartStorage.getCartCount());
        setAddedTip(true);
        setTimeout(() => setAddedTip(false), 2000);
      }
    } else {
      cartStorage.addToCart(product, quantity);
      setCartCount(cartStorage.getCartCount());
      setAddedTip(true);
      setTimeout(() => setAddedTip(false), 2000);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-50">
      <Header />
      <main className="flex-1 pt-24 pb-8">
        <div className="section-container">
          {/* Breadcrumb */}
          <nav className="flex items-center gap-2 text-sm text-gray-500 mb-6">
            <Link to="/" className="hover:text-[#1c5bff]">首页</Link>
            <span>/</span>
            <Link to="/shop" className="hover:text-[#1c5bff]">商城</Link>
            <span>/</span>
            <span className="text-gray-900">{product.productName}</span>
          </nav>

          {/* Product Info */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 p-6 lg:p-8">
              {/* Image */}
              {/* Image - 【修改】使用画廊组件 */}
              <ProductImageGallery 
                images={product.mainImage} 
                alt={product.productName} 
              />

              {/* Info */}
              <div className="flex flex-col">
                <span className="inline-block w-fit px-3 py-1 bg-[#1c5bff]/10 text-[#1c5bff] text-sm rounded-full mb-4">
                  {product.category || '工业材料'}
                </span>

                <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 mb-4">
                  {product.productName}
                </h1>

                <p className="text-gray-600 leading-relaxed mb-6" dangerouslySetInnerHTML={{ __html: product.description || '<p>暂无描述</p>' }}></p>

                {/* Price */}
                <div className="flex items-end gap-4 mb-6 pb-6 border-b border-gray-100">
                  <span className="text-3xl lg:text-4xl font-bold text-[#1c5bff]">
                    {formatPrice(product.price)}
                  </span>
                  <span className="text-sm text-gray-400">
                    / {product.unit || '吨'}
                  </span>
                </div>

                {/* Features */}
                <div className="flex flex-wrap gap-2 mb-6">
                  {specs.slice(0, 4).map((spec, index) => (
                    <span
                      key={index}
                      className="flex items-center gap-1 px-3 py-1 bg-green-50 text-green-700 text-sm rounded-full"
                    >
                      <Check className="w-4 h-4" />
                      {spec.label}: {spec.value}
                    </span>
                  ))}
                </div>

                {/* Stock & Sales */}
                <div className="flex items-center gap-6 text-sm text-gray-500 mb-6">
                  <span>库存: <strong className="text-gray-900">{product.stock}</strong> {product.unit || '吨'}</span>
                  <span>已售: <strong className="text-gray-900">{product.salesCount || 0}</strong> {product.unit || '吨'}</span>
                </div>

                {/* Quantity */}
                <div className="flex items-center gap-4 mb-6">
                  <span className="text-gray-600">采购数量:</span>
                  <div className="flex items-center border border-gray-200 rounded-lg">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="p-2 hover:bg-gray-100 transition-colors"
                    >
                      <Minus className="w-4 h-4" />
                    </button>
                    <input
                      type="number"
                      value={quantity}
                      onChange={(e) => setQuantity(Math.max(1, parseInt(e.target.value) || 1))}
                      className="w-16 text-center border-x border-gray-200 py-2 outline-none"
                    />
                    <button
                      onClick={() => setQuantity(quantity + 1)}
                      className="p-2 hover:bg-gray-100 transition-colors"
                    >
                      <Plus className="w-4 h-4" />
                    </button>
                  </div>
                  <span className="text-gray-500">{product.unit || '吨'}</span>
                </div>

                {/* Total */}
                <div className="flex items-center gap-2 mb-6">
                  <span className="text-gray-600">小计:</span>
                  <span className="text-xl font-bold text-[#1c5bff]">
                    {formatPrice(product.price * quantity)}
                  </span>
                </div>

                {/* Actions */}
                <div className="flex gap-4">
                  <button
                    onClick={handleAddToCart}
                    className="flex-1 btn-primary flex items-center justify-center gap-2"
                  >
                    <ShoppingCart className="w-5 h-5" />
                    加入购物车
                  </button>
                  <button className="flex-1 bg-[#ff6b35] text-white font-semibold px-6 py-3 rounded-lg hover:bg-[#e55a2b] transition-all duration-300 flex items-center justify-center gap-2">
                    <Phone className="w-5 h-5" />
                    立即询价
                  </button>
                </div>

                {/* Added tip */}
                {addedTip && (
                  <div className="mt-4 px-4 py-2 bg-green-50 text-green-700 rounded-lg text-sm flex items-center gap-2">
                    <Check className="w-4 h-4" />
                    已成功加入购物车
                  </div>
                )}

                {/* Contact */}
                <div className="flex items-center gap-6 mt-6 pt-6 border-t border-gray-100">
                  <div className="flex items-center gap-2 text-gray-600">
                    <Phone className="w-5 h-5 text-[#1c5bff]" />
                    <span>028-85002901</span>
                  </div>
                  <button className="flex items-center gap-2 text-[#1c5bff] hover:underline">
                    <MessageSquare className="w-5 h-5" />
                    在线咨询
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tabs */}
          <div className="bg-white rounded-2xl shadow-sm overflow-hidden mb-8">
            <div className="flex border-b border-gray-100">
              <button
                onClick={() => setActiveTab('specs')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'specs'
                    ? 'text-[#1c5bff] border-b-2 border-[#1c5bff]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                产品规格
              </button>
              <button
                onClick={() => setActiveTab('features')}
                className={`px-6 py-4 text-sm font-medium transition-colors ${
                  activeTab === 'features'
                    ? 'text-[#1c5bff] border-b-2 border-[#1c5bff]'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                产品特点
              </button>
            </div>

            <div className="p-6">
              {activeTab === 'specs' ? (
                specs.length > 0 ? (
                  <table className="w-full">
                    <tbody>
                      {specs.map((spec, index) => (
                        <tr
                          key={index}
                          className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}
                        >
                          <td className="px-4 py-3 text-gray-600 w-1/3">{spec.label}</td>
                          <td className="px-4 py-3 text-gray-900 font-medium">{spec.value}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <p className="text-gray-500 text-center py-8">暂无规格信息</p>
                )
              ) : (
                <div className="space-y-4">
                  <div className="text-gray-700 leading-relaxed"  
                    dangerouslySetInnerHTML={{ __html: product.description || '<p>暂无产品特点描述</p>' }} >
                    
                  </div>
                  <ul className="space-y-3">
                    {specs.map((spec, index) => (
                      <li key={index} className="flex items-start gap-3">
                        <Check className="w-5 h-5 text-green-500 flex-shrink-0 mt-0.5" />
                        <span className="text-gray-700"><strong>{spec.label}</strong>: {spec.value}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
